import React, { FC, useContext, useState } from 'react'
import { t, Trans } from '@lingui/macro'

import { staticLinks } from '../../../../../shared/linkBuilder'
import { navigateTo } from '../../../app/util/appHistory'
import { ButtonClean, ButtonDanger, ButtonDark } from 'front-end/src/components'
import Input from '../../../components/common/form/input/Input'
import { notification } from '../../../components/common/notifications/Notifications'
import { Drawer } from '../../../components/common/page/drawer/Drawer'
import DrawerItem from '../../../components/common/page/drawer/DrawerItem'
import { Modal } from '../../../components/common/modal/Modal'
import TagSelect, {
  TAG_TARGET_TYPES
} from '../../../components/common/tag/TagSelect'
import IconTrash from '../../../components/common/icon/IconTrash'
import { AccessibilityLabel } from '../../../components/common/AccessibilityLabel'
import { ManageSurveyContext } from './ManageSurveyContext'
import {
  useArchiveSurveyMutation,
  useUpdateSurveyMutation
} from './send/surveysOperations.codegen'

export const SurveySettingsDrawer: FC<{
  show?: boolean
  onHide: () => any
}> = (props) => {
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showCloseModal, setShowCloseModal] = useState<boolean>(false)

  const { survey, refetchSurvey } = useContext(ManageSurveyContext)
  const [updateSurvey] = useUpdateSurveyMutation()

  const [archiveSurvey, { loading }] = useArchiveSurveyMutation()

  return (
    <>
      <Drawer
        className="ManageSurveyDrawer"
        title={t`Settings`}
        show={props.show}
        onHide={() => {
          props.onHide()
          refetchSurvey()
        }}
      >
        <DrawerItem label={t`Name`}>
          <Input
            light
            title={t`Name`}
            defaultValue={survey.title}
            debouncedOnChange={async (value) => {
              const { data } = await updateSurvey({
                variables: {
                  id: survey.id,
                  input: {
                    title: value
                  }
                }
              })

              if (data && data.admin.survey.patch) {
                refetchSurvey()
              }
            }}
          />
        </DrawerItem>
        <DrawerItem
          label={t`Tags`}
          className="full-width"
          hint={t`Type to create new tags`}
        >
          <AccessibilityLabel text={t`Tags`}>
            <TagSelect
              target={{
                type: TAG_TARGET_TYPES.survey,
                id: survey.id
              }}
              defaultValue={survey.tags}
            />
          </AccessibilityLabel>
        </DrawerItem>
        {!survey.completed_at && (
          <DrawerItem label={t`Close this survey`}>
            <ButtonDark
              onClick={() => {
                setShowCloseModal(true)
              }}
              dataTest="CloseSurveyButton"
            >
              <Trans>Close</Trans>
            </ButtonDark>
          </DrawerItem>
        )}
        <DrawerItem className="danger" label={t`Delete this survey`}>
          <ButtonDanger
            onClick={() => {
              setShowDeleteModal(true)
            }}
            leftIcon={<IconTrash />}
            dataTest="DeleteSurveyButton"
          >
            <Trans>Delete</Trans>
          </ButtonDanger>
        </DrawerItem>
      </Drawer>

      <Modal
        header={t`Close survey`}
        show={showCloseModal}
        controls={[
          <ButtonClean
            onClick={() => {
              setShowCloseModal(false)
            }}
            key={1}
          >
            <Trans>Cancel</Trans>
          </ButtonClean>,
          <ButtonDanger
            key={2}
            isLoading={loading}
            onClick={async () => {
              const { data } = await updateSurvey({
                variables: {
                  id: survey.id,
                  input: {
                    completed_at: new Date()
                  }
                }
              })

              if (data && data.admin.survey.patch) {
                refetchSurvey()
                setShowCloseModal(false)
                notification.info(t`Survey was successfully closed`)
              }
            }}
            dataTest="CloseThisSurveyButton"
          >
            <Trans>Close this survey</Trans>
          </ButtonDanger>
        ]}
        onHide={() => {
          setShowCloseModal(false)
        }}
      >
        <p>
          <Trans>
            Are you sure you want to close this survey?
            <br />
            This action will stop any further user views/submissions and cannot
            be undone.
          </Trans>
        </p>
      </Modal>

      <Modal
        header={t`Delete survey`}
        show={showDeleteModal}
        controls={[
          <ButtonClean
            onClick={() => {
              setShowDeleteModal(false)
            }}
            key={3}
          >
            <Trans>Cancel</Trans>
          </ButtonClean>,
          <ButtonDanger
            key={4}
            isLoading={loading}
            onClick={async () => {
              const { data } = await archiveSurvey({
                variables: {
                  id: survey.id
                }
              })

              if (data && data.admin.survey.archive) {
                navigateTo(staticLinks.surveys)
                notification.info(t`Survey successfully deleted`)
              } else {
                notification.error(t`Unable to delete survey`)
              }
            }}
          >
            <Trans>Delete this survey</Trans>
          </ButtonDanger>
        ]}
        onHide={() => {
          setShowDeleteModal(false)
        }}
      >
        <p>
          <Trans>
            Are you sure you want to delete this survey?
            <br />
            This action cannot be undone
          </Trans>
        </p>
      </Modal>
    </>
  )
}
